import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import {
  Upload, Camera, Smartphone, Loader2, Sparkles, Trash2, Plus, FileText, RefreshCw, CheckCircle2, AlertCircle, ArrowLeft,
} from 'lucide-react';

// Converte un file immagine/PDF in payload { file_base64, mime_type }, ridimensionando le foto.
const fileToPayload = (file) => new Promise((resolve, reject) => {
  if (file.type === 'application/pdf') {
    const r = new FileReader();
    r.onload = () => resolve({ file_base64: String(r.result), mime_type: 'application/pdf' });
    r.onerror = reject;
    r.readAsDataURL(file);
    return;
  }
  const r = new FileReader();
  r.onload = () => {
    const img = new Image();
    img.onload = () => {
      const MAX = 1800;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const ratio = Math.min(MAX / width, MAX / height);
        width = Math.round(width * ratio); height = Math.round(height * ratio);
      }
      const c = document.createElement('canvas');
      c.width = width; c.height = height;
      c.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve({ file_base64: c.toDataURL('image/jpeg', 0.72), mime_type: 'image/jpeg' });
    };
    img.onerror = reject;
    img.src = String(r.result);
  };
  r.onerror = reject;
  r.readAsDataURL(file);
});

const emptyItem = () => ({ name: '', price: 0, description: '', category: '', ingredients: [], allergens: [] });

export const AiMenuImportDialog = ({ open, onOpenChange, onImported }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('upload');
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [items, setItems] = useState([]);
  const [view, setView] = useState('source'); // source | review
  const fileRef = useRef(null);

  // --- QR / smartphone ---
  const [qrToken, setQrToken] = useState(null);
  const [qrState, setQrState] = useState('idle'); // idle | waiting | expired
  const pollRef = useRef(null);

  // --- Fotocamera locale ---
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [camState, setCamState] = useState('idle'); // idle | on | error

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setCamState('idle');
  }, []);

  // Analizza un payload immagine/PDF con l'AI e accumula gli articoli estratti.
  const analyzePayload = useCallback(async (payload) => {
    setAnalyzing(true);
    try {
      const { data } = await api.post('/menu/analyze', payload, { timeout: 240000 });
      const found = data?.items || [];
      if (!found.length) { toast.error(t('aiImport.noneRecognized')); return; }
      setItems((prev) => [...prev, ...found.map((it) => ({ ...emptyItem(), ...it }))]);
      setView('review');
      toast.success(t('aiImport.recognized', { count: found.length }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('aiImport.analyzeFailed'));
    } finally {
      setAnalyzing(false);
    }
  }, [t]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    for (const f of files) {
      try {
        const payload = await fileToPayload(f);
        await analyzePayload(payload);
      } catch {
        toast.error(t('aiImport.readFileFailed', { name: f.name }));
      }
    }
  };

  // --- Fotocamera locale del dispositivo ---
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setCamState('on');
    } catch {
      setCamState('error');
    }
  };

  const shootPhoto = async () => {
    const video = videoRef.current;
    if (!video) return;
    const c = document.createElement('canvas');
    c.width = video.videoWidth; c.height = video.videoHeight;
    c.getContext('2d').drawImage(video, 0, 0, c.width, c.height);
    const payload = { file_base64: c.toDataURL('image/jpeg', 0.72), mime_type: 'image/jpeg' };
    stopCamera();
    await analyzePayload(payload);
  };

  // --- Handover smartphone via QR ---
  const startQrSession = useCallback(async () => {
    stopPolling();
    setQrState('idle');
    try {
      const { data } = await api.post('/menu/capture-sessions');
      setQrToken(data.token);
      setQrState('waiting');
      pollRef.current = setInterval(async () => {
        try {
          const { data: poll } = await api.get(`/menu/capture-sessions/${data.token}`);
          if (poll.status === 'uploaded' && poll.file_base64) {
            stopPolling();
            setQrState('idle');
            await analyzePayload({ file_base64: poll.file_base64, mime_type: poll.mime_type });
          }
        } catch (e) {
          if (e?.response?.status === 410 || e?.response?.status === 404) {
            stopPolling();
            setQrState('expired');
          }
        }
      }, 2500);
    } catch {
      toast.error(t('aiImport.qrGenFailed'));
    }
  }, [analyzePayload, stopPolling, t]);

  // Avvia/ferma risorse quando si cambia tab o si chiude il dialog.
  useEffect(() => {
    if (!open) return;
    if (tab === 'qr' && view === 'source') { startQrSession(); } else { stopPolling(); }
    if (tab !== 'camera') { stopCamera(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, open, view]);

  useEffect(() => () => { stopPolling(); stopCamera(); }, [stopPolling, stopCamera]);

  const handleClose = (v) => {
    if (!v) { stopPolling(); stopCamera(); }
    onOpenChange(v);
  };

  const resetAll = () => {
    setItems([]); setView('source'); setTab('upload'); setQrToken(null); setQrState('idle');
    stopPolling(); stopCamera();
  };

  const patchItem = (idx, patch) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const addRow = () => setItems((prev) => [...prev, emptyItem()]);

  const confirmImport = async () => {
    const valid = items.filter((it) => (it.name || '').trim());
    if (!valid.length) { toast.error(t('aiImport.noValidItems')); return; }
    setImporting(true);
    try {
      const { data } = await api.post('/menu/import-items', {
        items: valid.map((it) => ({
          name: it.name.trim(),
          price: Number(String(it.price).toString().replace(',', '.')) || 0,
          description: it.description || '',
          category: (it.category || '').trim(),
          ingredients: it.ingredients || [],
          allergens: it.allergens || [],
        })),
      });
      toast.success(t('aiImport.imported', { products: data.created_products, categories: data.created_categories }));
      onImported?.();
      resetAll();
      handleClose(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('aiImport.importFailed'));
    } finally {
      setImporting(false);
    }
  };

  const publicOrigin = window.location.origin;
  const captureUrl = qrToken ? `${publicOrigin}/cattura/${qrToken}` : '';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-testid="ai-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> {t('aiImport.title')}
          </DialogTitle>
          <DialogDescription>
            {view === 'source' ? t('aiImport.subtitleSource') : t('aiImport.subtitleReview')}
          </DialogDescription>
        </DialogHeader>

        {analyzing && (
          <div className="fixed inset-0 z-[60] bg-background/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3" data-testid="ai-analyzing">
            <Loader2 className="h-9 w-9 animate-spin text-primary" />
            <div className="text-sm font-medium">{t('aiImport.analyzing')}</div>
          </div>
        )}

        {view === 'source' ? (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="upload" data-testid="tab-upload"><Upload className="h-4 w-4 mr-2" /> {t('aiImport.tabUpload')}</TabsTrigger>
              <TabsTrigger value="camera" data-testid="tab-camera"><Camera className="h-4 w-4 mr-2" /> {t('aiImport.tabCamera')}</TabsTrigger>
              <TabsTrigger value="qr" data-testid="tab-qr"><Smartphone className="h-4 w-4 mr-2" /> {t('aiImport.tabPhone')}</TabsTrigger>
            </TabsList>

            {/* Carica file */}
            <TabsContent value="upload" className="pt-4">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full rounded-2xl border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors p-10 text-center"
                data-testid="upload-dropzone"
              >
                <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                <div className="font-semibold">{t('aiImport.uploadTitle')}</div>
                <div className="text-sm text-muted-foreground mt-1">{t('aiImport.uploadHint')}</div>
              </button>
              <input
                ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden"
                onChange={(e) => { const fs = e.target.files; e.target.value = ''; handleFiles(fs); }}
                data-testid="upload-file-input"
              />
            </TabsContent>

            {/* Fotocamera locale */}
            <TabsContent value="camera" className="pt-4">
              <div className="rounded-2xl border border-border overflow-hidden bg-black/90 aspect-video flex items-center justify-center relative">
                <video ref={videoRef} playsInline muted className={`w-full h-full object-contain ${camState === 'on' ? '' : 'hidden'}`} />
                {camState !== 'on' && (
                  <div className="text-center text-white/80 p-6">
                    <Camera className="h-10 w-10 mx-auto mb-2 opacity-80" />
                    {camState === 'error'
                      ? <div className="text-sm">{t('aiImport.cameraError')}</div>
                      : <div className="text-sm">{t('aiImport.cameraIdle')}</div>}
                  </div>
                )}
              </div>
              <div className="mt-3 flex gap-3">
                {camState === 'on' ? (
                  <Button className="flex-1 rounded-xl" onClick={shootPhoto} data-testid="camera-shoot"><Camera className="h-4 w-4 mr-2" /> {t('aiImport.cameraShoot')}</Button>
                ) : (
                  <Button className="flex-1 rounded-xl" onClick={startCamera} data-testid="camera-start"><Camera className="h-4 w-4 mr-2" /> {t('aiImport.cameraActivate')}</Button>
                )}
              </div>
            </TabsContent>

            {/* Smartphone esterno via QR */}
            <TabsContent value="qr" className="pt-4">
              <div className="flex flex-col items-center text-center">
                {qrState === 'expired' ? (
                  <div className="py-8">
                    <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
                    <div className="font-semibold mb-1">{t('aiImport.qrExpired')}</div>
                    <div className="text-sm text-muted-foreground mb-4">{t('aiImport.qrExpiredHint')}</div>
                    <Button className="rounded-xl" onClick={startQrSession} data-testid="qr-regenerate"><RefreshCw className="h-4 w-4 mr-2" /> {t('aiImport.qrNew')}</Button>
                  </div>
                ) : captureUrl ? (
                  <>
                    <div className="rounded-2xl border-4 border-primary p-4 bg-white" data-testid="qr-capture-code">
                      <QRCodeCanvas value={captureUrl} size={200} level="M" includeMargin={false} />
                    </div>
                    <div className="mt-4 max-w-sm text-sm text-muted-foreground">
                      {t('aiImport.qrInstructions')}
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-primary text-sm font-medium" data-testid="qr-waiting">
                      <Loader2 className="h-4 w-4 animate-spin" /> {t('aiImport.qrWaiting')}
                    </div>
                  </>
                ) : (
                  <div className="py-10 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          /* --- Anteprima modificabile --- */
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <Button variant="ghost" size="sm" className="rounded-lg" onClick={() => setView('source')} data-testid="review-back">
                <ArrowLeft className="h-4 w-4 mr-1.5" /> {t('aiImport.addMore')}
              </Button>
              <span className="text-sm text-muted-foreground">{t('aiImport.itemsCount', { count: items.length })}</span>
            </div>
            <ScrollArea className="h-[50vh] rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/80 backdrop-blur z-10">
                  <tr className="text-left">
                    <th className="p-2 font-semibold w-[26%]">{t('aiImport.colName')}</th>
                    <th className="p-2 font-semibold w-[12%]">{t('aiImport.colPrice')}</th>
                    <th className="p-2 font-semibold w-[18%]">{t('aiImport.colCategory')}</th>
                    <th className="p-2 font-semibold w-[20%]">{t('aiImport.colIngredients')}</th>
                    <th className="p-2 font-semibold w-[18%]">{t('aiImport.colAllergens')}</th>
                    <th className="p-2 w-[6%]" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx} className="border-t border-border align-top" data-testid={`review-row-${idx}`}>
                      <td className="p-1.5"><Input value={it.name} onChange={(e) => patchItem(idx, { name: e.target.value })} className="h-9" data-testid={`item-name-${idx}`} /></td>
                      <td className="p-1.5"><Input value={it.price} onChange={(e) => patchItem(idx, { price: e.target.value })} className="h-9 font-mono" inputMode="decimal" data-testid={`item-price-${idx}`} /></td>
                      <td className="p-1.5"><Input value={it.category} onChange={(e) => patchItem(idx, { category: e.target.value })} className="h-9" placeholder={t('aiImport.categoryPlaceholder')} data-testid={`item-category-${idx}`} /></td>
                      <td className="p-1.5"><Input value={(it.ingredients || []).join(', ')} onChange={(e) => patchItem(idx, { ingredients: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} className="h-9" placeholder={t('aiImport.commaSeparated')} /></td>
                      <td className="p-1.5"><Input value={(it.allergens || []).join(', ')} onChange={(e) => patchItem(idx, { allergens: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} className="h-9" placeholder={t('aiImport.commaSeparated')} /></td>
                      <td className="p-1.5 text-center">
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => removeItem(idx)} data-testid={`item-remove-${idx}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!items.length && (
                    <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t('aiImport.noItems')}</td></tr>
                  )}
                </tbody>
              </table>
            </ScrollArea>
            <div className="mt-2">
              <Button variant="outline" size="sm" className="rounded-lg" onClick={addRow} data-testid="review-add-row"><Plus className="h-4 w-4 mr-1.5" /> {t('aiImport.addRow')}</Button>
            </div>
          </div>
        )}

        <DialogFooter className="mt-2">
          {view === 'review' && (
            <Button onClick={confirmImport} disabled={importing || !items.length} className="rounded-xl" data-testid="confirm-import">
              {importing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('aiImport.importing')}</> : <><CheckCircle2 className="h-4 w-4 mr-2" /> {t('aiImport.confirmImport')}</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
